import { useEffect, useRef } from 'react';
import { useAutoConnectWallet } from './walletCompat';
import { Tracker } from '../utils/tracker';
function getMarketSymbol(pathname) {
    const match = pathname.match(/^\/market\/(.+)$/);
    if (!match)
        return '';
    try {
        return decodeURIComponent(match[1]);
    }
    catch (_a) {
        return match[1];
    }
}
/** Shown as $Page — market routes use the generic name only. */
function getPageProperty(pathname) {
    if (pathname === '/')
        return 'Lend';
    if (pathname === '/strategies')
        return 'Strategies';
    if (pathname === '/portfolio')
        return 'Portfolio';
    if (pathname === '/ALPHA')
        return 'ALPHA';
    if (pathname === '/stsui')
        return 'stSUI';
    if (pathname === '/cetus-compensation')
        return 'Cetus Comp';
    if (pathname.startsWith('/vote'))
        return 'Vote';
    if (pathname.startsWith('/analytics'))
        return 'Analytics';
    if (pathname.startsWith('/market/'))
        return 'Market Details';
    return 'Other';
}
/** Stored after each view for the next event's $PreviousPage — includes which market when applicable. */
function getHistoryLabel(pathname) {
    if (pathname === '/')
        return 'Lend';
    if (pathname === '/strategies')
        return 'Strategies';
    if (pathname === '/portfolio')
        return 'Portfolio';
    if (pathname === '/ALPHA')
        return 'ALPHA';
    if (pathname === '/stsui')
        return 'stSUI';
    if (pathname === '/cetus-compensation')
        return 'Cetus Comp';
    if (pathname.startsWith('/vote'))
        return 'Vote';
    if (pathname.startsWith('/analytics'))
        return 'Analytics';
    if (pathname.startsWith('/market/')) {
        const symbol = getMarketSymbol(pathname);
        return symbol ? `Market Details (${symbol})` : 'Market Details';
    }
    return 'Other';
}
export function usePageViewTracker(params) {
    const { pathname, walletAddress, themeName } = params;
    const autoConnectStatus = useAutoConnectWallet();
    // Use refs so wallet/theme changes don't retrigger page views — only route changes do
    const walletAddressRef = useRef(walletAddress);
    const themeNameRef = useRef(themeName);
    walletAddressRef.current = walletAddress;
    themeNameRef.current = themeName;
    const previousPageRef = useRef('Null');
    // Route the last event was emitted for; null until the first one fires. Also the
    // de-duplication guard — `autoConnectStatus` is a dependency below, and it can leave
    // 'attempted' again (connecting an already-connected wallet re-enters 'reconnecting'),
    // which would otherwise re-emit an event for the current route.
    const lastEmittedPathRef = useRef(null);
    // One effect for the initial page view AND subsequent route changes — the two were
    // identical apart from their gate, and keeping them separate meant a route change could
    // only ever fire after the initial event had.
    //
    // Gated on auto-connect having settled so $Wallet is accurate: a returning user's session
    // restore is still in flight during the first renders.
    //
    // Trade-off: routes visited before auto-connect settles are not emitted — the first event
    // reports whichever route is current once it settles. Accurate connected-status is worth
    // more than counting those early navigations, since emitting them early would mislabel
    // every returning user as "Not Connected".
    useEffect(() => {
        if (autoConnectStatus === 'idle')
            return;
        if (lastEmittedPathRef.current === pathname)
            return;
        const marketSymbol = getMarketSymbol(pathname);
        Tracker.page(Object.assign({ $Wallet: walletAddressRef.current ? 'Connected' : 'Not Connected', $Page: getPageProperty(pathname), $PreviousPage: previousPageRef.current, $Theme: themeNameRef.current === 'dark' ? 'Dark' : 'Light', $Path: pathname }, (marketSymbol && { $Market: marketSymbol })));
        previousPageRef.current = getHistoryLabel(pathname);
        lastEmittedPathRef.current = pathname;
    }, [pathname, autoConnectStatus]);
}
